import { createContext, useContext } from 'react';
import type { CONNECTION_TYPE_UI } from '../tokens';
import type { ConnectionModeEnum } from '../types/types';
import type { User } from '../types/User';

export const UserContext = createContext<UserContextProps | undefined>(undefined);
UserContext.displayName = 'UserContext';

export function useUserContext() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('Missing UserContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

export type ApiKeyResult = {
  Key: string;
  Secret: string;
  WhitelistedIPs?: string;
};

export interface UserContextProps {
  isLoaded: boolean;
  user: User;
  orgWsEndpoint: string;
  orgApiEndpoint: string;

  createApiKey(whitelistedIPs?: string): Promise<ApiKeyResult>;

  deleteApiKey(): Promise<void>;

  updateApiKeyWhitelistedIPs(whitelistedIPs: string | undefined): Promise<void>;

  updateApiKeyRoles(roles: string[]): Promise<void>;

  refreshUser(): void;

  listMarketConfigs(): Promise<MarketConfig[]>;

  listMarketCredentials(): Promise<MarketCredential[]>;

  createMarketCredential(marketName: string, values: CredentialsFormParams): Promise<any>;

  updateMarketCredential(marketName: string, credentialName: string, values: CredentialsFormParams): Promise<any>;

  patchMarketCredential(marketName: string, credentialName: string, values: CredentialsFormParams): Promise<any>;

  deleteMarketCredential(marketName: string, credentialName: string): Promise<any>;
}

export type CredentialsFormParams = { [key: string]: string | Blob | undefined };

export interface MarketCredential {
  Name: string;
  Label: string; // Treat as a Display Name
  ConnectionType: string;
  CredentialID: number;
  Market: string;
  Mode: ConnectionModeEnum;
  Attributes?: Record<string, string>;
  SecretsUpdatedAt?: string;
  AlertMutedUntil?: string;
}

export type CredentialTemplateFields = {
  label: string;
  name: string;
  required: boolean;
  cleartext?: boolean;
  preview?: { first?: number; last?: number };
  type?: 'file' | 'textarea' | 'checkbox' | 'select';
  values?: string[];
};

export type CredentialTemplate = {
  type: keyof typeof CONNECTION_TYPE_UI;
  fields: CredentialTemplateFields[];
  displayName: string;
};

export interface MarketConfig {
  credentials: CredentialTemplate[];
  feesDescription: string;
  feesUrl: string;
  guide: {
    header: string;
    image: string;
    text: string;
  }[];
  label: string;
  name: string;
  settingsUrl: string;
}
